import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useWebSocket } from '@/features/conversations/hooks/useWebSocket';
import { useNotificationStore } from './notificationStore';

const REASON_LABEL: Record<string, string> = {
  refund: 'Refund request',
  return: 'Return request',
  complaint: 'Complaint',
  human_request: 'Wants a human',
  order_problem: 'Order problem',
  critical: 'Needs attention',
};

/**
 * Global notification feed for the header bell. Mount once (in DashboardLayout).
 * Listens for human-handoff escalations (and new leads) over WebSocket, pushes
 * them into the notification store, and raises a toast for escalations so the
 * agent notices immediately even if the bell is closed.
 */
export const useNotificationFeed = () => {
  const { subscribe } = useWebSocket();
  const add = useNotificationStore((s) => s.add);

  useEffect(() => {
    const offEscalation = subscribe('conversation_escalated', (d: any) => {
      const label = REASON_LABEL[d?.reason as string] || 'Needs human';
      const order = d?.orderNumber ? ` · Order ${d.orderNumber}` : '';
      add({
        type: 'escalation',
        title: `${label}${order}`,
        body: `${d?.customerName || 'A customer'}: ${(d?.preview || '').slice(0, 90)}`,
        conversationId: d?.conversationId,
        timestamp: d?.timestamp,
      });
      toast(`${label} — needs a human`, { icon: '🚨', duration: 6000 });
    });

    const offLead = subscribe('new_lead', (d: any) => {
      const lead = d?.lead || d;
      add({
        type: 'lead',
        title: 'New lead',
        body: `${lead?.customerName || 'A customer'}: ${(lead?.message || '').slice(0, 90)}`,
        conversationId: undefined,
        timestamp: undefined,
      });
    });

    return () => {
      offEscalation();
      offLead();
    };
  }, [subscribe, add]);
};
